// src/games/Sudoku/Game/index.tsx
import { message } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoTriangleSharp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import { GameHeader } from '../../../components/GameHeader';
import { Sidebar } from '../../../components/Sidebar';
import { useAuth } from '../../../hooks/auth';
import { usePopup } from '../../../hooks/popup';
import { FinishModal } from '../../core/finish/FinishModal';
import type { FinishMemo } from '../../core/finish/finishMemo';
import { revealConfirm } from '../../core/reveal/revealConfirm';
import { useGameSession } from '../../core/session/useGameSession';
import { SudokuLoad, finishSudoku, loadSudokuGame, saveSudokuProgress, sudokuResult } from '../api';
import { SudokuCell, fillableCount } from '../rules';
import { useSudoku } from '../SudokuContext';
import { Board } from './Board';
import { Container, Content, GameContent, Rules } from './styles';

interface SudokuProps {
  /** Id do jogo, vindo da rota (que remonta a tela com `key` quando ele muda). */
  jogoId: number;
}

export const Sudoku: React.FC<SudokuProps> = ({ jogoId }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const { mountPopup } = usePopup();
  const navigate = useNavigate();
  const { matrix, load, isBoardFilled, setIsSudokuFinished, canRevealFocused, revealFocused, revealAll, clear } =
    useSudoku();

  const [loaded, setLoaded] = useState<SudokuLoad | null>(null);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const matrixRef = useRef(matrix);
  matrixRef.current = matrix;

  useEffect(() => {
    let alive = true;
    loadSudokuGame(jogoId, userId)
      .then((res) => {
        if (!alive) return;
        if (!res) {
          message.warning('Este jogo ainda não tem conteúdo.');
          navigate('/');
          return;
        }
        load(res.cells);
        setIsSudokuFinished(res.finished !== null);
        setLoaded(res);
      })
      .catch(() => {
        if (!alive) return;
        message.error('Não foi possível abrir o jogo. Tente de novo.');
        navigate('/');
      });
    return () => {
      alive = false;
    };
  }, [jogoId, userId, load, setIsSudokuFinished, navigate]);

  const session = useGameSession({
    ready: loaded !== null,
    savedSeconds: loaded?.savedSeconds ?? 0,
    alreadyFinished: loaded?.finished ?? null,
    onSaveProgress: (seconds) =>
      userId !== undefined
        ? saveSudokuProgress({
            userId,
            jogoId,
            cells: matrixRef.current,
            finished: false,
            seconds,
          })
        : undefined,
  });
  const { finish, isPlaying } = session;
  const playingRef = useRef(isPlaying);
  playingRef.current = isPlaying;

  useEffect(() => {
    if (isBoardFilled && isPlaying) message.info('Tabuleiro completo! Use "Verificar acertos" para pontuar.');
  }, [isBoardFilled, isPlaying]);

  const finishWith = useCallback(
    (cells: SudokuCell[][]) => {
      if (!playingRef.current) return;
      setIsSudokuFinished(true);
      const memo: FinishMemo = {}; // o "Tentar de novo" reusa: manda só o que falhou
      void finish(sudokuResult(cells), async (seconds) => {
        if (userId === undefined) return;
        await finishSudoku({ userId, jogoId, cells, seconds }, memo);
      });
    },
    [finish, jogoId, setIsSudokuFinished, userId]
  );

  const askVerify = () =>
    mountPopup({
      title: 'Verificar acertos?',
      message: 'O jogo termina e o resultado vale para o ranking.',
      type: 'alegre',
      buttons: [
        {
          buttonText: 'Verificar',
          buttonFunction: () => finishWith(matrixRef.current),
        },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  const askRevealNumber = () => {
    if (!canRevealFocused()) {
      message.info('Clique numa casa vazia do tabuleiro e depois em "Revelar número".');
      return;
    }
    mountPopup(
      revealConfirm({
        scope: 'cell',
        unit: 'casa',
        title: 'Revelar número?',
        onConfirm: () => {
          if (playingRef.current) revealFocused();
        },
      })
    );
  };

  const askRevealAll = () =>
    mountPopup(
      revealConfirm({
        scope: 'all',
        unit: 'casa',
        title: 'Revelar resposta?',
        onConfirm: () => {
          if (playingRef.current) finishWith(revealAll());
        },
      })
    );

  const askClear = () =>
    mountPopup({
      title: 'Limpar o jogo?',
      message: 'Os números que você digitou serão apagados. Casas reveladas continuam reveladas.',
      type: 'atencao',
      buttons: [
        { buttonText: 'Limpar', buttonFunction: clear },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  const openRules = () => setIsRulesModalOpen(true);

  // Enquanto carrega não há ações (nem "Ver resultado" sem resultado).
  const menuItems =
    session.status === 'loading'
      ? []
      : isPlaying
        ? [
            { buttonText: 'Verificar acertos', buttonFunctions: askVerify },
            { buttonText: 'Revelar número', buttonFunctions: askRevealNumber },
            { buttonText: 'Revelar resposta', buttonFunctions: askRevealAll },
            { buttonText: 'Limpar jogo', buttonFunctions: askClear },
            { buttonText: 'Regras', buttonFunctions: openRules },
          ]
        : [
            { buttonText: 'Ver resultado', buttonFunctions: session.showResult },
            { buttonText: 'Regras', buttonFunctions: openRules },
          ];

  const maxScore = fillableCount(matrix);
  const currentScore = session.result?.points ?? 0;
  const size = loaded?.shape.size ?? 9;
  const boxW = loaded?.shape.boxW ?? 3;
  const boxH = loaded?.shape.boxH ?? 3;
  const ruleSum = (size * (size + 1)) / 2;

  return (
    <Container>
      <GameHeader
        type="SUDOKU"
        maxScore={maxScore}
        currentScore={currentScore}
        seconds={session.seconds}
        menuItems={menuItems}
      />
      <Content>
        {loaded && (
          <>
            <GameContent>
              <div>
                <div>
                  <Board bigSquare={loaded.bigSquare} values={loaded.values} />
                </div>

                <Sidebar
                  type="SUDOKU"
                  maxScore={maxScore}
                  currentScore={currentScore}
                  seconds={session.seconds}
                  menuItems={menuItems}
                />
              </div>
            </GameContent>
            <p style={{ background: '#D1A14C' }}>
              VER MAIS COMENTÁRIOS <IoTriangleSharp size={12} />
            </p>

            <Rules isVisible={isRulesModalOpen}>
              <div>
                <header>
                  <h1>Regras</h1>
                  <button onClick={() => setIsRulesModalOpen(false)}>
                    <h1>X</h1>
                  </button>
                </header>
                <main>
                  <span>
                    O objetivo do jogo é preencher e completar todos os quadrados da tabela com os números de 1 a {size}
                    . O desafio se encontra nas restrições impostas pelas regras do Sudoku, que podem ser lidas abaixo:
                  </span>
                  <br /> <br />
                  <span>
                    <strong>1ª Regra: Cada linha deve conter os números de 1 a {size}, sem repetir!</strong>
                    <br />
                  </span>
                  <span>
                    O jogador deve preencher cada linha da tabela de modo que não existam números duplicados no final,
                    no entanto, a ordem númerica é irrelevante.
                  </span>
                  <br /> <br />
                  <span>
                    <strong>2ª Regra: Cada coluna também deve conter os números de 1 a {size}, sem repetições!</strong>
                    <br />
                  </span>
                  <span>
                    O jogador deve preencher cada coluna da tabela com números de 1 a {size} sem repetições por coluna!
                  </span>
                  <br /> <br />
                  <span>
                    <strong>3ª Regra: Cada número pode estar presente apenas uma vez por grupo!</strong>
                    <br />
                  </span>
                  <span>
                    Uma tabela de Sudoku tradicional é formada por {size} linhas e {size} colunas ({size} x {size}), e é
                    divida em {size} grupos menores de {boxH} linhas e {boxW} colunas ({boxW} x {boxH}). Os números de 1
                    a {size} podem estar presentes apenas uma vez em cada um destes grupos.
                  </span>
                  <br /> <br />
                  <span>
                    <strong>4ª Regra: O valor da soma de cada linha, coluna ou grupo é de {ruleSum}!</strong>
                    <br />
                  </span>
                  <span>
                    Para descobrir quais os números faltantes em cada linha, coluna ou grupo, o jogador pode contá-los
                    ou então somá-los! Quando não existem dígitos duplicados, a soma individual de cada linha, coluna ou
                    grupo é sempre {ruleSum}:
                    <br />
                    <footer>
                      <span>
                        {Array.from({ length: size }, (_, i) => i + 1).join(' + ')} = {ruleSum}
                      </span>
                    </footer>
                  </span>
                </main>
              </div>
            </Rules>
          </>
        )}
      </Content>

      <FinishModal
        visible={session.modalVisible}
        result={session.result}
        seconds={session.seconds}
        error={session.error}
        busy={session.status === 'finishing'}
        onRetry={session.retry}
        onClose={session.hideResult}
        onHome={() => navigate('/')}
      />
    </Container>
  );
};
